import { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  DialogTrigger,
  Popover,
} from '@backstage/ui';
import styles from './chart.module.css';

export type RangeOption = { label: string; days: number };

export const RANGE_OPTIONS: RangeOption[] = [
  { label: '7D', days: 7 },
  { label: '1M', days: 30 },
  { label: '2M', days: 60 },
  { label: '3M', days: 90 },
];

const CUSTOM_MIN = 2;
const CUSTOM_MAX = 365;

export type HistoryRangeSelectorProps = {
  /** Currently selected number of days. */
  selectedDays: number;
  /** Called when the user picks a preset range or applies a custom value. */
  onSelectedDaysChange: (days: number) => void;
};

/**
 * Range selector shared by the check and track history charts. Renders the
 * preset range toggles (7D/1M/2M/3M) plus a "Custom" popover that lets the
 * user enter an arbitrary number of days.
 */
export const HistoryRangeSelector = ({
  selectedDays,
  onSelectedDaysChange,
}: HistoryRangeSelectorProps) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [customInput, setCustomInput] = useState(selectedDays);

  // Keep the custom input in sync when the caller changes selectedDays via
  // presets so the popover never shows a stale value.
  useEffect(() => {
    setCustomInput(selectedDays);
  }, [selectedDays]);

  const isCustomSelected = !RANGE_OPTIONS.some(o => o.days === selectedDays);
  const selectedLabel = RANGE_OPTIONS.find(o => o.days === selectedDays)?.label;

  const applyCustom = () => {
    const clamped = Math.min(Math.max(customInput, CUSTOM_MIN), CUSTOM_MAX);
    onSelectedDaysChange(clamped);
    setCustomInput(clamped);
    setCustomOpen(false);
  };

  return (
    <Flex justify="end" gap="1" align="center">
      <ToggleButtonGroup
        className={styles.toggleButtons}
        selectionMode="single"
        selectedKeys={selectedLabel ? [selectedLabel] : []}
        onSelectionChange={keys => {
          const key = [...keys][0] as string | undefined;
          const option = RANGE_OPTIONS.find(o => o.label === key);
          if (option) onSelectedDaysChange(option.days);
        }}
        aria-label="Time range"
      >
        {RANGE_OPTIONS.map(option => (
          <ToggleButton key={option.label} id={option.label} size="small">
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <DialogTrigger isOpen={customOpen} onOpenChange={setCustomOpen}>
        <Button
          variant={isCustomSelected ? 'primary' : 'secondary'}
          size="small"
          className={styles.customButton}
        >
          Custom
        </Button>
        <Popover placement="bottom end" hideArrow>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text variant="body-small" weight="bold">
                Number of days
              </Text>
              <Text color="secondary" variant="body-small">
                Enter a value from {CUSTOM_MIN} to {CUSTOM_MAX}
              </Text>
            </Flex>
            <Flex align="center" gap="2">
              <Button
                variant="secondary"
                size="small"
                aria-label="Decrease"
                onPress={() => setCustomInput(v => Math.max(v - 1, CUSTOM_MIN))}
              >
                −
              </Button>
              <input
                type="number"
                min={CUSTOM_MIN}
                max={CUSTOM_MAX}
                value={customInput}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (!Number.isNaN(v)) setCustomInput(v);
                }}
                style={{
                  width: 60,
                  textAlign: 'center',
                  border: '1px solid var(--bui-border-default, #ccc)',
                  borderRadius: '4px',
                  padding: '4px',
                  fontSize: 14,
                }}
              />
              <Button
                variant="secondary"
                size="small"
                aria-label="Increase"
                onPress={() => setCustomInput(v => Math.min(v + 1, CUSTOM_MAX))}
              >
                +
              </Button>
            </Flex>
            <Button variant="primary" size="small" onPress={applyCustom}>
              Apply
            </Button>
          </Flex>
        </Popover>
      </DialogTrigger>
    </Flex>
  );
};
